import { useEffect } from 'react';

/**
 * Card3DEffect
 * Globaler, dezenter 3D-/Glas-Effekt für alle Karten auf der Seite — ohne
 * jede Komponente einzeln anzufassen.
 *
 *  - Desktop (hover): sanftes 3D-Kippen Richtung Mauszeiger + Glas/Glow.
 *  - Mobile: minimale, langsame Eigenbewegung (nur sichtbare Karten).
 *  - Erkennt nachgeladene (lazy) Sektionen via MutationObserver.
 *  - Respektiert prefers-reduced-motion.
 *
 * Performance-Design (wichtig für TBT/Lighthouse):
 *  - Startet erst NACH window.load + Idle — konkurriert nicht mit dem
 *    kritischen Ladefenster.
 *  - MutationObserver löst keine Sofort-Scans aus, sondern markiert nur
 *    "dirty" und plant EINEN gebündelten Idle-Scan.
 *  - Scans trennen strikt Lese- (offsetWidth/offsetHeight) und Schreib-
 *    phase (classList) → kein Layout-Thrashing.
 *
 * Klassen/Styles dazu stehen in index.css (.svb-card-3d ...).
 */
const Card3DEffect = () => {
  useEffect(() => {
    const reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768 ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const MAX = 6; // maximale Neigung in Grad

    const idle = (cb, timeout) =>
      ('requestIdleCallback' in window)
        ? window.requestIdleCallback(cb, { timeout: timeout || 2000 })
        : setTimeout(cb, 200);

    const clsOf = (el) =>
      typeof el.className === 'string'
        ? el.className
        : (el.className && el.className.baseVal) || '';

    // Reiner Klassen-/Struktur-Check — KEINE Layout-Reads hier
    const isCandidate = (el) => {
      if (!el || el.nodeType !== 1 || el.dataset.svbCard) return false;
      const cls = clsOf(el);
      if (!/rounded-(xl|2xl|3xl)/.test(cls)) return false;
      if (!/border|bg-black|bg-white|backdrop-blur|glass/.test(cls)) return false;
      if (el.matches('button,a,input,textarea,select,nav,svg,img')) return false;
      if (el.closest('nav, button, a, .admin-modal-overlay, .admin-modal-container')) return false;
      // Kein Tilt in schwebenden Overlays (Admin-Panel, Prompts, Sticky-UI)
      if (el.closest('.fixed')) return false;
      // Explizites Opt-out für einzelne Karten (z.B. Token Distribution)
      if (el.closest('[data-no-tilt]')) return false;
      // Karten mit eingebetteten Charts/Frames nicht kippen (Price Chart, Swap-Widgets)
      if (el.querySelector('iframe, canvas, video, embed')) return false;
      return true;
    };

    const enhanced = new Set();
    let io = null;
    if (isMobile && !reduce && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) =>
          e.target.classList.toggle('svb-card-inview', e.isIntersecting));
      }, { threshold: 0.15 });
    }

    let disposed = false;
    let scanScheduled = false;

    // Gebündelter Scan: Phase 1 nur LESEN (Größen), Phase 2 nur SCHREIBEN.
    const runScan = () => {
      scanScheduled = false;
      if (disposed || enhanced.size > 250) return;

      const nodes = document.querySelectorAll(
        '[class*="rounded-xl"],[class*="rounded-2xl"],[class*="rounded-3xl"]'
      );
      const candidates = [];
      for (let i = 0; i < nodes.length; i++) {
        if (isCandidate(nodes[i])) candidates.push(nodes[i]);
      }
      if (!candidates.length) return;

      // Phase 1: alle Layout-Reads am Stück (ein einziger Layout-Pass)
      const sized = [];
      for (let i = 0; i < candidates.length; i++) {
        const el = candidates[i];
        const w = el.offsetWidth, h = el.offsetHeight;
        if (w >= 170 && h >= 80) sized.push(el); // kleine Pills/Buttons ausschließen
      }

      // Phase 2: alle Writes am Stück (im nächsten Frame)
      if (!sized.length) return;
      requestAnimationFrame(() => {
        if (disposed) return;
        for (let i = 0; i < sized.length; i++) {
          const el = sized[i];
          if (enhanced.has(el)) continue;
          enhanced.add(el);
          el.dataset.svbCard = '1';
          el.classList.add('svb-card-3d');
          if (isMobile) {
            // versetzte, langsame Eigenbewegung -> nicht alle synchron
            el.style.animationDelay = (-Math.random() * 6).toFixed(2) + 's';
            if (io) io.observe(el);
          }
        }
      });
    };

    const scheduleScan = () => {
      if (scanScheduled || disposed) return;
      scanScheduled = true;
      idle(runScan, 1500);
    };

    // ── Desktop: Maus-Tilt (Delegation + rAF-Drosselung) ──
    let activeCard = null;
    let raf = 0;
    let lastX = 0, lastY = 0, lastTarget = null;

    const apply = () => {
      raf = 0;
      const card = lastTarget && lastTarget.closest
        ? lastTarget.closest('.svb-card-3d') : null;
      if (!card) {
        if (activeCard) { activeCard.style.transform = ''; activeCard = null; }
        return;
      }
      activeCard = card;
      const rect = card.getBoundingClientRect();
      const px = (lastX - rect.left) / rect.width - 0.5;
      const py = (lastY - rect.top) / rect.height - 0.5;
      card.style.transform =
        `perspective(900px) rotateY(${(px * MAX).toFixed(2)}deg) rotateX(${(-py * MAX).toFixed(2)}deg)`;
    };

    const onMove = (e) => {
      lastX = e.clientX; lastY = e.clientY; lastTarget = e.target;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeaveCard = (e) => {
      const card = e.target.closest && e.target.closest('.svb-card-3d');
      if (card) card.style.transform = '';
      if (activeCard === card) activeCard = null;
    };

    // MutationObserver: nur dirty-markieren, Scan gebündelt im Idle
    const mo = new MutationObserver(() => scheduleScan());

    let started = false;
    const start = () => {
      if (started || disposed) return;
      started = true;
      scheduleScan();
      // Nachzügler-Scan für langsam nachladende Lazy-Sektionen
      setTimeout(scheduleScan, 2500);
      mo.observe(document.body, { childList: true, subtree: true });
      if (!reduce && !isMobile) {
        document.addEventListener('pointermove', onMove, { passive: true });
        document.addEventListener('pointerout', onLeaveCard, { passive: true });
      }
    };

    // Erst nach vollständigem Load + Idle starten — hält das kritische
    // Ladefenster (FCP/LCP/TBT) frei. Der Effekt ist rein dekorativ.
    if (document.readyState === 'complete') {
      idle(start, 3000);
    } else {
      window.addEventListener('load', () => idle(start, 3000), { once: true });
    }

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      mo.disconnect();
      if (io) io.disconnect();
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerout', onLeaveCard);
    };
  }, []);

  return null;
};

export default Card3DEffect;
